import * as THREE from 'three'

import RenderItem from 'Renderer/RenderItem'
import GhostItem from 'Renderer/GhostItem'

import Fading from 'utils/fading'

import { DEFAULT_THREE_NATIVE_LAYER, STATIC_STACKABLES_THREE_NATIVE_LAYER } from 'Renderer/constants'

const fullOpacity = 0.75
const defaultColor = new THREE.Color(0x4595FF)
const highlightColor = new THREE.Color(0x0A8A00)

class StackableSurfaceItem extends RenderItem {
    constructor(id, renderer, plane, parent, onGhostRender) {
        super(id, renderer)

        this.plane = plane

        this.parent = parent
        this.onGhostRender = onGhostRender

        // Static stackable surfaces are assigned a separate rendering layer to isolate shadows
        this.ThreeNtvLayer = parent ? DEFAULT_THREE_NATIVE_LAYER : STATIC_STACKABLES_THREE_NATIVE_LAYER

        this.highlighted = false
        this.layer = 1
        this.renderer = renderer
        this.createThreeObject()

        const planeId = this.plane && this.plane.id
        this.ghost = new GhostItem(id, renderer, this, onGhostRender, planeId)
    }

    createThreeObject() {
        if (!this.renderer) {
            return
        }

        let geometry

        const { type } = this.plane.parameters

        if( type === "circle" ) {
            const { radius } = this.plane.parameters
            geometry = new THREE.CircleGeometry(radius, 32)
        }
        else if( type === "polygon" ){
            geometry = new THREE.BufferGeometry()

            const points = this.plane.parameters.vertices.map(
                point => new THREE.Vector2(point[0], point[1])
            )

            geometry.vertices = points

            const triangles = THREE.ShapeUtils.triangulateShape(points, [])
            const positions = []
            const normals = []

            triangles.forEach(triangle => {
                const p0 = points[triangle[0]]
                const p1 = points[triangle[1]]
                const p2 = points[triangle[2]]

                positions.push(p0.x)
                positions.push(p0.y)
                positions.push(0)

                positions.push(p1.x)
                positions.push(p1.y)
                positions.push(0)

                positions.push(p2.x)
                positions.push(p2.y)
                positions.push(0)

                normals.push(0, 0, 1)
                normals.push(0, 0, 1)
                normals.push(0, 0, 1)
            })

            geometry.setAttribute(
                'position',
                new THREE.BufferAttribute(
                    new Float32Array(positions), 3))

            geometry.setAttribute(
                'normal',
                new THREE.BufferAttribute(
                    new Float32Array(normals), 3))
        }

        this.material = new THREE.MeshBasicMaterial({
            color: defaultColor,
            side: THREE.DoubleSide,
            transparent: true,
            depthWrite: false,
            opacity: 1.0,
        })

        this.renderObject = new THREE.Mesh(geometry, this.material)
        this.renderObject.name = `StackablePlane${this.id}`
        this.renderObject.stackablePlaneId = this.id

        this.renderObject.userData.isStaticSurface = ! this.parent

        if (!this.parent) {
            // Add a shadow layer to static stackable surfaces since they have not a virtual object
            const shadowPlane = this.renderObject.clone()
            shadowPlane.receiveShadow = true
            shadowPlane.material = new THREE.ShadowMaterial()
            shadowPlane.material.opacity = 0.3
            shadowPlane.material.depthWrite = false
            shadowPlane.layers.set(this.ThreeNtvLayer)
            this.renderObject.add(shadowPlane)
        }

        const t = this.plane.transform
        const depthDelta = 0.0035

        const mat = new THREE.Matrix4().set(
            t[0][0], t[0][1], t[0][2], t[0][3],
            t[1][0], t[1][1], t[1][2], t[1][3] + depthDelta,
            t[2][0], t[2][1], t[2][2], t[2][3],
            0, 0, 0, 1)

        this.renderObject.applyMatrix4(mat)

        if (this.parent) {
            const parentItem = this.renderer.getRenderItem(this.parent.uniqueId)
            if (parentItem) {
                parentItem.addChild(this)
            }
        }

        this.renderObject.updateMatrixWorld()
        this.renderer.addStackingSurface(this)
    }

    frame() {
        const t = this.plane.transform

        return [
            [ t[0][0], t[1][0], t[2][0] ],
            [ t[0][1], t[1][1], t[2][1] ],
            [ t[0][2], t[1][2], t[2][2] ]
        ]
    }

    // eslint-disable-next-line class-methods-use-this
    setPosition() {}

    setVisible(visible) {
        if (visible === this.visible || !this.renderObject) {
            return
        }

        this.visible = visible
        if (visible) { // if not, object will need to fade out first
            this.highlighted = false
            this.material.color = defaultColor
        }

        this.startFade(visible)
        this.Renderer.onChildUpdate()
    }

    startFade(isFadeIn) {
        if (!this.material) {
            return
        }
        // easeOutExp
        const ease = isFadeIn ? 
            (t) => 1 - 2 ** (-10 * t) :
            (t) => 2 ** (10 * t - 10)

        let totalTime = 0.75
        if (this.material.appearFading) {
            totalTime *= this.material.appearFading.t
            this.material.appearFading.stop()
        }

        this.material.appearFading = new Fading(totalTime,
            (t) => {
                const timeFactor = isFadeIn ? t : (1.0 - t)
                this.material.opacity = fullOpacity * ease(timeFactor)
                this.Renderer.onChildUpdate()
            },
            () => {
                this.material.appearFading = null
            }
        )
    }

    setHighlighted(highlight) {
        if (this.highlighted === highlight) {
            return
        }
        if (!this.Renderer || !this.material) {
            return
        }

        this.highlighted = highlight

        const startColor = new THREE.Color().copy(this.material.color)
        const targetColor = new THREE.Color().copy(highlight ? highlightColor : defaultColor)
        // this.material.color = color
        // this.Renderer.onChildUpdate()

        const ease = (t) => 1 - 2 ** (-10 * t)
        let totalTime = 0.75
        if (this.material.highlightFading) {
            totalTime *= this.material.highlightFading.t
            this.material.highlightFading.stop()
        }
        this.material.highlightFading = new Fading(totalTime,
            (t) => {
                this.material.color = startColor.lerp(targetColor, ease(t))
                this.Renderer.onChildUpdate()
            },
            () => {
                this.material.highlightFading = null
                this.material.color = targetColor
                this.Renderer.onChildUpdate()
            }
        )
    }

    showGhost(furnitureData) {
        if (!furnitureData ) {
            return
        }
        if (this.ghost && this.ghost.visible) {
            return
        }
        this.ghost.setFurnitureData(furnitureData)
        this.ghost.setVisible(true)
    }

    hideGhost() {
        this.ghost.setVisible(false)
    }

    setGhostPosition(xy) {
        this.ghost.setPosition(xy)
    }

    endIsNigh() {
        if (this.material) {
            if (this.material.appearFading) {
                this.material.appearFading.stop()
                this.material.appearFading = null
            }
            if (this.material.highlightFading) {
                this.material.highlightFading.stop()
                this.material.highlightFading = null
            }
            this.material.dispose()
            this.material = null
        }
        super.endIsNigh()
    }
}

export default StackableSurfaceItem
